// React, Router and Services
import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import productService from "services/product-service";
import { useAbility } from "@casl/react";
import { AbilityContext } from "Can";

// E4A React components
import Footer from "e4acomponents/Footer";
import DashboardLayout from "e4acomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "e4acomponents/Navbars/DashboardNavbar";
import ChangesDialog from "e4acomponents/ChangesComponents/ChangesDialog";

// Material Dashboard and MUI components
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import CircularProgress from "@mui/material/CircularProgress";

// Card components
import ProductDetailsCard from "./product-details-card";
import ProductModelCard from "./product-model-card";
import FilesCard from "./files-card";
import { DispatchCard } from "../components/dispatchCard";
import { OrderCard } from "../components/orderCard";

// Helper functions
import { convertDate } from "../components/helperFunctions";

export default function ViewSpecificProduct() {
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const [product, setProduct] = useState(null);
    const [dispatch, setDispatch] = useState(null);
    const [order, setOrder] = useState(null);
    const [title, setTitle] = useState("");
    // State to manage which cards to display
    const [cardsToDisplay, setCardsToDisplay] = useState({
        model: true,
        product: true,
        order: false,
        dispatch: false,
        files: false
    });

    const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
    const [dialogProps, setDialogProps] = useState({
        docId: "",
        docType: "",
        title: "",
    });

    const ability = useAbility(AbilityContext);


    // Fetch product details on component mount
    useEffect(() => {
        (async () => {
            try {
                setIsLoading(true);
                const response = await productService.getProductById(id);
                const included = response.relationship;
                setProduct(response.data.attributes);

                let updatedToDisplay = { ...cardsToDisplay };

                if (included.order) {
                    const cardData = getOrderCardData(included.order, included.organization.name);
                    setOrder(cardData);
                    updatedToDisplay.order = true;
                } if (included.dispatch) {
                    const cardData = getDispatchCardData(included.dispatch, included.organization.name);
                    setDispatch(cardData);
                    updatedToDisplay.dispatch = true;
                } if (response.data.attributes.certifications?.length > 0) {
                    updatedToDisplay.files = true;
                }
                setCardsToDisplay(updatedToDisplay);

                const pageTitle = response.data.attributes.serialNumber.replace(/-/g, '–');
                setTitle(pageTitle ? pageTitle : "AAA-XXX-YYYYY");

            } catch (error) {
                console.error("Error fetching product details:", error);
            } finally {
                setIsLoading(false);
            }
        })();
    }, []);

    // Function to get order card data
    const getOrderCardData = (order, orgName) => {
        return {
            _id: order._id,
            id: order.order_num,
            orderNum: order.order_num,
            description: order.desc,
            organization: orgName,
            numProducts: order.qty,
            totalPrice: order.totalPrice,
            assignedOn: convertDate(order.org_changed_at),
            items: order.items,
        }
    };

    // Function to get dispatch card data
    const getDispatchCardData = (dispatch, orgName) => {
        return {
            ...dispatch,
            organization: orgName,
        }
    };

    // Handler to open the history (changes) dialog
    const handleOpenHistoryDialog = (props) => {
        setDialogProps(props);
        setOpenHistoryDialog(true);
    }

    // Handler to close the history (changes) dialog
    const handleCloseHistoryDialog = () => {
        setDialogProps({
            docId: "",
            docType: "",
            title: "",
        });
        setOpenHistoryDialog(false);
    }

    // Memoized DispatchCard to avoid unnecessary re-renders; only when not loading
    const memoizedDispatchCard = useMemo(() => {
        if (isLoading || !dispatch) return null;
        return (
            <DispatchCard
                dispatch={dispatch}
                viewButton={false}
                defaultBorders={true}
            />
        );
    }, [dispatch, isLoading]);

    // Memoized OrderCard; only when not loading
    const memoizedOrderCard = useMemo(() => {
        if (isLoading || !order) return null;
        return ability.can("view","orders-history") ? (
            <OrderCard
                order={order}
                viewButton={false}
                viewContent={false}
                viewHistory={true}
                onViewHistory={handleOpenHistoryDialog}
                defaultBorders={true}
                size="small"
            />
        ) : (
            <OrderCard
                order={order}
                viewButton={false}
                viewContent={false}
                defaultBorders={true}
                size="small"
            />
        );
    }, [order, isLoading]);

    return (
        <DashboardLayout>
            <DashboardNavbar breadcrumbTitle={title} />

            {isLoading ?
                (
                    <MDBox py={3}>
                        <CircularProgress />
                    </MDBox>
                ) : (
                    <MDBox py={3}>

                        {/* Changes History Dialog */}
                        <ChangesDialog
                            docId={dialogProps.docId}
                            docType={dialogProps.docType}
                            title={dialogProps.title}
                            open={openHistoryDialog}
                            onClose={handleCloseHistoryDialog}
                        />

                        <Grid container spacing={3} >
                            <Grid item xs={12} >

                                {/* Title of the page */}
                                <MDBox p={3} justifyContent="center" alignItems="center" display="flex">
                                    <MDTypography variant="h3" fontWeight="bold">
                                        {product?.serialNumber || "Product Page"}
                                    </MDTypography>
                                </MDBox>

                            </Grid>

                            {/* Left column */}
                            <Grid item xs={12} md={6}>

                                {/* Product Model Card */}
                                {cardsToDisplay.model && (
                                    <MDBox>
                                        <ProductModelCard
                                            id={product?.partCode}
                                        />
                                    </MDBox>
                                )}

                                {/* Product Details Card*/}
                                {cardsToDisplay.product && (
                                    <MDBox mt={3}>
                                        {ability.can("view", "products-history") ? (
                                            <ProductDetailsCard
                                                product={product}
                                                viewHistory={true}
                                                onViewHistory={handleOpenHistoryDialog}
                                            />
                                        ) : (
                                            <ProductDetailsCard
                                                product={product}
                                            />
                                        )}
                                    </MDBox>
                                )}

                            </Grid>

                            {/* Right column */}
                            <Grid item xs={12} md={6}>

                                {/* Order Card */}
                                {cardsToDisplay.order && (
                                    <MDBox>
                                        {memoizedOrderCard}
                                    </MDBox>
                                )}

                                {/* Dispatch Card */}
                                {cardsToDisplay.dispatch && (
                                    <MDBox mt={3}>
                                        {memoizedDispatchCard}
                                    </MDBox>
                                )}

                                {/* Files Card */}
                                {cardsToDisplay.files && (
                                    <MDBox mt={3}>
                                        <FilesCard
                                            files={product?.certifications || []}
                                            title="Product Certifications"
                                        />
                                    </MDBox>
                                )}

                            </Grid>

                        </Grid>

                    </MDBox>)}

            <Footer />

        </DashboardLayout>
    )
}